import { flattenOverlay } from "./color-utils";
import { BORDER_RADIUS, SPACINGS, TYPOGRAPHY } from "./design-constants";
import { COLOR_TOKENS } from "./mui-colors";

// MUI component configs — used in mui-themes.ts → theme.components
// Each constant maps to the overrides/defaultProps for a specific MUI component.

// ─── MuiButton ────────────────────────────────────────────────────────────────

export const MUI_BUTTON_CONFIG = {
  borderRadius: BORDER_RADIUS.XXL,

  lg: {
    paddingHorizontal: SPACINGS[6],
    paddingVertical: SPACINGS[2],
    gap: SPACINGS[3],
    paddingIconButton: SPACINGS[2]
  },

  md: {
    paddingHorizontal: SPACINGS[4],
    paddingVertical: SPACINGS[1],
    gap: SPACINGS[1],
    paddingIconButton: SPACINGS[1.5]
  },

  sm: {
    paddingHorizontal: SPACINGS[2],
    paddingVertical: SPACINGS[1],
    gap: SPACINGS[1],
    paddingIconButton: SPACINGS[1]
  },

  variants: {
    // contained in MUI
    primary: {
      borderRadius: BORDER_RADIUS.XXL,
      background: COLOR_TOKENS.MAIN.primary,
      color: COLOR_TOKENS.MAIN.white,
      hoverBackground: COLOR_TOKENS.ACTION.hoverReversed,
      pressedBackground: COLOR_TOKENS.ACTION.pressedReversed,
      disabledBackground: COLOR_TOKENS.ACTION.disabled
    },

    outlined: {
      borderRadius: BORDER_RADIUS.XXL,
      background: COLOR_TOKENS.BACKGROUND.white,
      color: COLOR_TOKENS.MAIN.primary,
      borderColor: COLOR_TOKENS.MAIN.primary,
      hoverBackground: `linear-gradient(0deg, ${COLOR_TOKENS.ACTION.hover} 0%, ${COLOR_TOKENS.ACTION.hover} 100%), ${COLOR_TOKENS.MAIN.primary}`,
      pressedBackground: `linear-gradient(0deg, ${COLOR_TOKENS.ACTION.pressed} 0%, ${COLOR_TOKENS.ACTION.pressed} 100%), ${COLOR_TOKENS.MAIN.primary}`,
      disabledColor: flattenOverlay(COLOR_TOKENS.MAIN.primary, COLOR_TOKENS.ACTION.disabled)
    },

    // custom variant
    flat: {
      borderRadius: BORDER_RADIUS.XXL,
      background: COLOR_TOKENS.BACKGROUND.page,
      color: COLOR_TOKENS.MAIN.primary,
      hoverBackground: `linear-gradient(0deg, ${COLOR_TOKENS.ACTION.hover} 0%, ${COLOR_TOKENS.ACTION.hover} 100%), ${COLOR_TOKENS.MAIN.primary}`,
      pressedBackground: `linear-gradient(0deg, ${COLOR_TOKENS.ACTION.pressed} 0%, ${COLOR_TOKENS.ACTION.pressed} 100%), ${COLOR_TOKENS.MAIN.primary}`,
      disabledColor: flattenOverlay(COLOR_TOKENS.MAIN.primary, COLOR_TOKENS.ACTION.disabled)
    },

    text: {
      borderRadius: BORDER_RADIUS.XXL,
      color: COLOR_TOKENS.MAIN.primary,
      hoverBackground: `linear-gradient(0deg, ${COLOR_TOKENS.ACTION.hover} 0%, ${COLOR_TOKENS.ACTION.hover} 100%), ${COLOR_TOKENS.MAIN.primary}`,
      pressedBackground: `linear-gradient(0deg, ${COLOR_TOKENS.ACTION.pressed} 0%, ${COLOR_TOKENS.ACTION.pressed} 100%), ${COLOR_TOKENS.MAIN.primary}`,
      disabledColor: flattenOverlay(COLOR_TOKENS.MAIN.primary, COLOR_TOKENS.ACTION.disabled)
    }
  }
} as const;

// ─── MuiIconButton ────────────────────────────────────────────────────────────

export const MUI_ICON_BUTTON_CONFIG = {
  borderRadius: BORDER_RADIUS.XXL,
  padding: SPACINGS[2],

  sizes: {
    sm: { size: SPACINGS[6], iconSize: SPACINGS[4] },
    md: { size: SPACINGS[8], iconSize: SPACINGS[5] },
    lg: { size: SPACINGS[10], iconSize: SPACINGS[6] }
  },

  variants: {
    // contained in MUI (primary filled)
    primary: {
      background: COLOR_TOKENS.MAIN.primary,
      color: COLOR_TOKENS.MAIN.white,
      hoverBackground: COLOR_TOKENS.ACTION.hoverReversed,
      pressedBackground: COLOR_TOKENS.ACTION.pressedReversed,
      disabledBackground: COLOR_TOKENS.ACTION.disabled
    },

    outlined: {
      background: COLOR_TOKENS.BACKGROUND.white,
      color: COLOR_TOKENS.MAIN.primary,
      borderColor: COLOR_TOKENS.MAIN.primary,
      hoverBackground: `linear-gradient(0deg, ${COLOR_TOKENS.ACTION.hover} 0%, ${COLOR_TOKENS.ACTION.hover} 100%), ${COLOR_TOKENS.MAIN.primary}`,
      pressedBackground: COLOR_TOKENS.ACTION.pressed,
      disabledColor: flattenOverlay(COLOR_TOKENS.MAIN.primary, COLOR_TOKENS.ACTION.disabled)
    },

    flat: {
      background: COLOR_TOKENS.BACKGROUND.page,
      color: COLOR_TOKENS.MAIN.primary,
      hoverBackground: `linear-gradient(0deg, ${COLOR_TOKENS.ACTION.hover} 0%, ${COLOR_TOKENS.ACTION.hover} 100%), ${COLOR_TOKENS.MAIN.primary}`,
      pressedBackground: `linear-gradient(0deg, ${COLOR_TOKENS.ACTION.pressed} 0%, ${COLOR_TOKENS.ACTION.pressed} 100%), ${COLOR_TOKENS.MAIN.primary}`,
      disabledBackground: `linear-gradient(0deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 255, 255, 0.96) 100%), ${COLOR_TOKENS.MAIN.primary};`,
      disabledColor: flattenOverlay(COLOR_TOKENS.MAIN.primary, COLOR_TOKENS.ACTION.disabled)
    },

    text: {
      color: COLOR_TOKENS.MAIN.primary,
      hoverBackground: `linear-gradient(0deg, ${COLOR_TOKENS.ACTION.hover} 0%, ${COLOR_TOKENS.ACTION.hover} 100%), ${COLOR_TOKENS.MAIN.primary}`,
      pressedBackground: `linear-gradient(0deg, ${COLOR_TOKENS.ACTION.pressed} 0%, ${COLOR_TOKENS.ACTION.pressed} 100%), ${COLOR_TOKENS.MAIN.primary}`,
      disabledColor: flattenOverlay(COLOR_TOKENS.MAIN.primary, COLOR_TOKENS.ACTION.disabled)
    }
  }
} as const;

// ─── MuiTextField (input) ─────────────────────────────────────────────────────

export const MUI_TEXT_FIELD_CONFIG = {} as const;

// ─── MuiOutlinedInput ─────────────────────────────────────────────────────────

export const MUI_OUTLINED_INPUT_CONFIG = {} as const;

// ─── MuiInputLabel ────────────────────────────────────────────────────────────

export const MUI_INPUT_LABEL_CONFIG = {} as const;

// ─── MuiFormLabel ─────────────────────────────────────────────────────────────

export const MUI_FORM_LABEL_CONFIG = {} as const;

// ─── MuiSelect ────────────────────────────────────────────────────────────────

export const MUI_SELECT_CONFIG = {} as const;

// ─── MuiAutocomplete ──────────────────────────────────────────────────────────

export const MUI_AUTOCOMPLETE_CONFIG = {} as const;

// ─── MuiTypography ────────────────────────────────────────────────────────────

export const MUI_TYPOGRAPHY_CONFIG = {} as const;

// ─── MuiSvgIcon ───────────────────────────────────────────────────────────────

export const MUI_SVG_ICON_CONFIG = {} as const;

// ─── MuiTab ───────────────────────────────────────────────────────────────────

export const MUI_TAB_CONFIG = {
  iconSize: 16
} as const;

// ─── MuiCheckbox ──────────────────────────────────────────────────────────────

export const MUI_CHECKBOX_CONFIG = {} as const;

// ─── MuiRadio ─────────────────────────────────────────────────────────────────

export const MUI_RADIO_CONFIG = {} as const;

// ─── MuiDivider ───────────────────────────────────────────────────────────────

export const MUI_DIVIDER_CONFIG = {} as const;

// ─── MuiToggleButtonGroup ─────────────────────────────────────────────────────

export const MUI_TOGGLE_BUTTON_GROUP_CONFIG = {} as const;

// ─── Popup ────────────────────────────────────────────────────────────────────

export const MUI_POPUP_CONFIG = {
  borderRadius: BORDER_RADIUS.MD,
  padding: SPACINGS[8],
  gap: SPACINGS[5]
} as const;

// ─── Menu ─────────────────────────────────────────────────────────────────────

export const MUI_MENU_CONFIG = {
  borderRadius: BORDER_RADIUS.MD,
  iconSize: 18,

  lg: {
    paddingHorizontal: SPACINGS[3],
    paddingVertical: SPACINGS[2],
    gap: SPACINGS[2]
  },

  md: {
    paddingHorizontal: SPACINGS[3],
    paddingVertical: SPACINGS[1.5],
    gap: SPACINGS[2]
  }
} as const;

// ─── Table ────────────────────────────────────────────────────────────────────

export const MUI_TABLE_CONFIG = {
  dividerWidth: 1,
  borderRadius: BORDER_RADIUS.MD,
  borderColor: COLOR_TOKENS.STROKE.divider,
  rowHoverBackground: COLOR_TOKENS.BACKGROUND.idle,

  lg: {
    header: {
      paddingHorizontal: SPACINGS[5],
      paddingVertical: SPACINGS[2]
    },
    cell: {
      paddingHorizontal: SPACINGS[5],
      paddingVertical: SPACINGS[3]
    }
  },

  md: {
    header: {
      paddingHorizontal: SPACINGS[4],
      paddingVertical: SPACINGS[2]
    },
    cell: {
      paddingHorizontal: SPACINGS[4],
      paddingVertical: SPACINGS[2]
    }
  }
} as const;

// ─── MuiTableCell ─────────────────────────────────────────────────────────────

export const MUI_TABLE_CELL_CONFIG = {
  head: {
    paddingHorizontal: SPACINGS[4],
    paddingVertical: SPACINGS[2],
    color: COLOR_TOKENS.MAIN.primary,
    background: COLOR_TOKENS.BACKGROUND.idle,
    fontWeight: 500,
    fontSize: TYPOGRAPHY.body1.fontSize,
    lineHeight: TYPOGRAPHY.body1.lineHeight
  },
  body: {
    paddingHorizontal: SPACINGS[4],
    paddingVertical: SPACINGS[3],
    color: COLOR_TOKENS.MAIN.primary,
    borderColor: COLOR_TOKENS.STROKE.divider,
    fontWeight: 400,
    fontSize: TYPOGRAPHY.body1.fontSize,
    lineHeight: TYPOGRAPHY.body1.lineHeight
  }
} as const;

// ─── MuiList ──────────────────────────────────────────────────────────────────

export const MUI_LIST_CONFIG = {} as const;

// ─── Card ─────────────────────────────────────────────────────────────────────

export const MUI_CARD_CONFIG = {
  borderRadius: BORDER_RADIUS.MD,
  background: COLOR_TOKENS.BACKGROUND.white,
  paddingGap: SPACINGS[4]
} as const;

// ─── Input ────────────────────────────────────────────────────────────────────

export const MUI_INPUT_CONFIG = {
  borderRadius: BORDER_RADIUS.MD,

  md: {
    paddingHorizontal: SPACINGS[2],
    paddingVertical: SPACINGS[2]
  },

  lg: {
    paddingHorizontal: SPACINGS[2],
    paddingVertical: SPACINGS[2]
  },

  xl: {
    paddingHorizontal: SPACINGS[2],
    paddingVertical: SPACINGS[3]
  }
} as const;

// ─── MuiChip ──────────────────────────────────────────────────────────────────

export const MUI_CHIP_CONFIG = {
  sm: {
    paddingAll: SPACINGS[1],
    paddingLabel: SPACINGS[1]
  }
} as const;

// ─── Badge ────────────────────────────────────────────────────────────────────

export const MUI_BADGE_CONFIG = {
  padding: SPACINGS[1]
} as const;
